/**
 * The "projected current month" column.
 *
 * The Analysis table shows completed months as actuals and future months as
 * budget-driven forecasts. The live month sits awkwardly between the two: its
 * cells hold real transactions, but only the ones that have happened so far,
 * so a mid-August column reads as a cheap month and — worse — every forecast
 * month chains its opening balance off that half-finished figure.
 *
 * Rather than overwrite the live month's actuals, this module synthesises an
 * extra column immediately after it holding the same month's *landing*
 * figures: where each category ends up once the rest of the month plays out.
 * The column carries a suffixed month key ('Aug-26~proj') so it is a distinct
 * key everywhere months are used as map keys or sorted, while still naming
 * the calendar month it belongs to.
 *
 * Because the synthetic column sorts between the live month and the first
 * forecast month, the existing balance chain picks it up as the forecast
 * months' predecessor with no further wiring — future months rebase onto the
 * projected month-end automatically.
 *
 * Category landings come from utils/categoryForecast (the same math the
 * Budgets page's "Fcast:" figure shows), so the two surfaces agree.
 */

import { forecastCategory } from './categoryForecast';
import { generateOccurrences } from './generateOccurrences';
import { categoryAccountShares } from './categoryAccountUtils';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export const PROJECTED_SUFFIX = '~proj';

/** 'Aug-26' -> 'Aug-26~proj'. */
export const makeProjectedMonth = (month) => `${month}${PROJECTED_SUFFIX}`;

/** True for a synthetic projected-month key. */
export const isProjectedMonth = (month) =>
  typeof month === 'string' && month.endsWith(PROJECTED_SUFFIX);

/**
 * The calendar month a key refers to: 'Aug-26~proj' -> 'Aug-26'. Safe on any
 * month key, so parse sites can call it unconditionally.
 */
export const baseMonthOf = (month) =>
  (isProjectedMonth(month) ? month.slice(0, -PROJECTED_SUFFIX.length) : month);

/**
 * Column header for a projected key: just the calendar month, same as the
 * actual column it follows. The two are told apart by the forecast styling
 * (shaded, italic), not by the label.
 */
export const projectedMonthLabel = (month) => baseMonthOf(month);

/**
 * Chronological comparator for month keys that tolerates the projected
 * suffix, placing a projected column immediately after the actual month it
 * projects. Every place months are sorted must use this — a naive
 * `split('-')` comparator reads the suffix as part of the year.
 */
export const compareMonthKeys = (a, b) => {
  const rank = (key) => {
    const [monthAbbr, yearStr] = baseMonthOf(key).split('-');
    return (2000 + parseInt(yearStr, 10)) * 12 + MONTH_NAMES.indexOf(monthAbbr);
  };
  const diff = rank(a) - rank(b);
  if (diff !== 0) return diff;
  return (isProjectedMonth(a) ? 1 : 0) - (isProjectedMonth(b) ? 1 : 0);
};

/** True when a month key belongs to the given two-digit year ('26'). */
export const monthInYear = (month, yearShort) => baseMonthOf(month).endsWith(`-${yearShort}`);

/** 'Aug-26' -> that month's first and last day. */
const monthRange = (month) => {
  const [monthAbbr, yearStr] = baseMonthOf(month).split('-');
  const monthIndex = MONTH_NAMES.indexOf(monthAbbr);
  if (monthIndex < 0) return null;
  const year = 2000 + parseInt(yearStr, 10);
  return { start: new Date(year, monthIndex, 1), end: new Date(year, monthIndex + 1, 0) };
};

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Landing figures for every category in the live month, shaped like the
 * forecastData maps so the table's existing forecast accessors can read them.
 *
 * Each category's landing is forecastCategory()'s figure — spend so far, plus
 * committed action occurrences still to come, plus the estimated remainder —
 * using the category's own forecastMethod preference. Categories the forecast
 * can't speak to fall back to their actual-to-date figure, which is the
 * honest floor.
 *
 * @returns {{
 *   incomeCategories: Object<string, number>,
 *   expenseCategories: Object<string, number>,
 *   summary: {income: number, expenses: number},
 *   terms: Object<string, Object>,  // forecastCategory() result per category
 * }}
 */
export const computeProjectedMonthAmounts = ({
  month,
  categories = [],
  transactions = [],
  actions = [],
  budgetData,
  today = new Date(),
}) => {
  const range = monthRange(month);
  const empty = { incomeCategories: {}, expenseCategories: {}, summary: { income: 0, expenses: 0 }, terms: {} };
  if (!range || !budgetData) return empty;

  const baseMonth = baseMonthOf(month);
  const methodFor = (categoryId) => categories.find((c) => c.id === categoryId)?.forecastMethod;

  const result = { incomeCategories: {}, expenseCategories: {}, summary: { income: 0, expenses: 0 }, terms: {} };

  [
    { type: 'Income', source: budgetData.incomeCategories, target: result.incomeCategories, bucket: 'income' },
    { type: 'Expense', source: budgetData.expenseCategories, target: result.expenseCategories, bucket: 'expenses' },
  ].forEach(({ type, source, target, bucket }) => {
    Object.keys(source || {}).forEach((categoryId) => {
      const actual = source[categoryId]?.[baseMonth] || 0;
      const terms = forecastCategory({
        categoryId,
        type,
        periodStart: range.start,
        periodEnd: range.end,
        transactions,
        actions,
        method: methodFor(categoryId),
        today,
      });
      // Never land below what has already happened.
      const amount = terms ? Math.max(terms.forecast, actual) : actual;
      target[categoryId] = round2(amount);
      if (terms) result.terms[categoryId] = terms;
      result.summary[bucket] += amount;
    });
  });

  result.summary.income = round2(result.summary.income);
  result.summary.expenses = round2(result.summary.expenses);
  return result;
};

/**
 * How much each account still moves between today and the end of the live
 * month — the delta to add to its actual-to-date balance to get a projected
 * month-end balance.
 *
 * Two sources, kept disjoint so nothing is counted twice:
 *
 * - The remainder of each category's landing (landing minus actual-to-date),
 *   signed by type and attributed to accounts by where that category's
 *   transactions actually landed this month. Categories with no activity yet
 *   fall back to their budgets' account routing (an account-specific budget
 *   lands whole; an "All Accounts" budget splits evenly), then to the primary
 *   account.
 * - Remaining occurrences of scheduled actions. Income and expense actions
 *   that carry a category are already inside their category's landing (they
 *   are forecastCategory's "committed" term), so only uncategorised ones are
 *   added here. Transfers never touch a category and always count — they are
 *   net zero across accounts but move balances between them.
 *
 * @returns {Object<string, number>} accountId -> signed movement
 */
export const computeProjectedAccountDeltas = ({
  month,
  accounts = [],
  budgets = [],
  categories = [],
  actions = [],
  transactions = [],
  projectedAmounts,
  budgetData,
  today = new Date(),
}) => {
  const range = monthRange(month);
  const deltas = {};
  accounts.forEach((a) => { deltas[a.id] = 0; });
  if (!range || !projectedAmounts || !budgetData) return deltas;

  const baseMonth = baseMonthOf(month);
  const activeAccounts = accounts.filter((a) => a.isActive);
  const primaryAccount = accounts.find((a) => a.isPrimary) || activeAccounts[0] || accounts[0];

  const monthEnd = range.end;
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tomorrow = new Date(now);
  tomorrow.setDate(tomorrow.getDate() + 1);

  // ------------------------------------------- category remainders by account
  const addDelta = (accountId, amount) => {
    if (!accountId || !amount) return;
    deltas[accountId] = (deltas[accountId] || 0) + amount;
  };

  // Fallback attribution: the category's budgets name an account, or split.
  const budgetMixFor = (categoryId, type) => {
    const categoryBudgets = budgets.filter(
      (b) => b.categoryId === categoryId && b.type === type && b.isActive
    );
    if (!categoryBudgets.length) return null;
    const named = categoryBudgets.filter((b) => b.accountId);
    if (named.length) {
      const mix = {};
      named.forEach((b) => { mix[b.accountId] = (mix[b.accountId] || 0) + 1; });
      Object.keys(mix).forEach((id) => { mix[id] /= named.length; });
      return mix;
    }
    const share = 1 / (activeAccounts.length || 1);
    const mix = {};
    activeAccounts.forEach((a) => { mix[a.id] = share; });
    return mix;
  };

  [
    { type: 'Income', sign: 1, amounts: projectedAmounts.incomeCategories, actuals: budgetData.incomeCategories },
    { type: 'Expense', sign: -1, amounts: projectedAmounts.expenseCategories, actuals: budgetData.expenseCategories },
  ].forEach(({ type, sign, amounts, actuals }) => {
    Object.keys(amounts || {}).forEach((categoryId) => {
      const landing = amounts[categoryId] || 0;
      const actual = actuals?.[categoryId]?.[baseMonth] || 0;
      const remainder = landing - actual;
      if (remainder <= 0) return;

      // Same attribution rule the forecast months use, so this column and the
      // ones after it route a category's money the same way.
      const mix =
        categoryAccountShares({
          categoryId,
          type,
          transactions,
          accountIds: activeAccounts.map((a) => a.id),
          today,
        }) ||
        budgetMixFor(categoryId, type) ||
        (primaryAccount ? { [primaryAccount.id]: 1 } : null);
      if (!mix) return;

      Object.entries(mix).forEach(([accountId, share]) => {
        addDelta(accountId, sign * remainder * share);
      });
    });
  });

  // ------------------------------------------------------- remaining actions
  const categorisedIds = new Set([
    ...Object.keys(projectedAmounts.incomeCategories || {}),
    ...Object.keys(projectedAmounts.expenseCategories || {}),
  ]);

  actions
    .filter((a) => a && a.isActive !== false && !a.isCompleted)
    .forEach((action) => {
      const isTransfer = action.type === 'transfer';
      // Categorised income/expense actions already sit in their category's
      // landing figure as forecastCategory's committed term.
      if (!isTransfer && action.categoryId && categorisedIds.has(action.categoryId)) return;

      const amount = Math.abs(Number(action.amount) || 0);
      if (!amount) return;

      const occurrences = generateOccurrences(action, tomorrow, monthEnd).length;
      if (!occurrences) return;
      const total = amount * occurrences;

      if (action.type === 'income') {
        const target =
          action.destinationAccountId ||
          action.sourceAccountId ||
          primaryAccount?.id;
        addDelta(target, total);
      } else if (action.type === 'expense') {
        const target =
          action.sourceAccountId ||
          action.destinationAccountId ||
          primaryAccount?.id;
        addDelta(target, -total);
      } else if (isTransfer) {
        addDelta(action.sourceAccountId, -total);
        addDelta(action.destinationAccountId, total);
      }
    });

  Object.keys(deltas).forEach((id) => { deltas[id] = round2(deltas[id]); });
  return deltas;
};
